import { useMemo } from 'react';

import { useUser } from '@/lib/auth';

/**
 * CTA 시나리오 타입
 * - guest: 비로그인 사용자
 * - owner: 로그인한 퀴즈 소유자 (잠금 해제된 퀴즈)
 * - owner-locked: 로그인한 퀴즈 소유자 (잠금된 퀴즈 - 플랜 만료 + 유료 템플릿)
 * - viewer: 로그인한 비소유자 (잠금 해제된 퀴즈)
 * - viewer-locked: 로그인한 비소유자 (잠금된 퀴즈 - 플랜 만료 + 유료 템플릿)
 */
export type CTAScenario =
  | 'guest'
  | 'owner'
  | 'owner-locked'
  | 'viewer'
  | 'viewer-locked';

interface UseCTAScenarioParams {
  quizOwnerId: string;
  isLocked: boolean;
}

interface UseCTAScenarioReturn {
  scenario: CTAScenario;
}

/**
 * useCTAScenario - 사용자 컨텍스트에 따라 표시할 CTA 시나리오를 결정
 *
 * @description
 * 사용자 인증 상태, 퀴즈 소유권, 잠금 상태를 기반으로
 * 적절한 CTA 시나리오를 결정하는 중앙화된 CTA 결정 로직 훅
 *
 * 결정 트리:
 * 1. 사용자 없음 (게스트):
 *    - isLocked이면 → 'viewer-locked' (복사만 가능, 입장 불가)
 *    - 아니면 → 'guest' (복사 + 입장 가능)
 * 2. 사용자 존재 && user.id === quizOwnerId:
 *    - isLocked이면 → 'owner-locked'
 *    - 아니면 → 'owner'
 * 3. 사용자 존재 && user.id !== quizOwnerId:
 *    - isLocked이면 → 'viewer-locked'
 *    - 아니면 → 'viewer'
 *
 * @param quizOwnerId - 퀴즈 소유자의 사용자 ID
 * @param isLocked - 퀴즈가 잠겨있는지 여부 (플랜 기반 또는 유료 템플릿)
 * @returns CTA 시나리오를 포함하는 객체
 *
 * @example
 * ```tsx
 * const { scenario } = useCTAScenario({
 *   quizOwnerId: quizData.ownerId,
 *   isLocked: quizData.requiresPremiumPlan && !hasPremiumAccess
 * });
 *
 * return <CTAButtonGroup scenario={scenario} />;
 * ```
 */
export const useCTAScenario = ({
  quizOwnerId,
  isLocked,
}: UseCTAScenarioParams): UseCTAScenarioReturn => {
  const { user } = useUser();

  const scenario = useMemo<CTAScenario>(() => {
    // 결정 1: 게스트 사용자 (로그인하지 않음)
    if (!user) {
      // 게스트 사용자가 잠긴 퀴즈를 보는 경우 viewer-locked 시나리오 적용
      // (복사만 가능, 입장 불가)
      return isLocked ? 'viewer-locked' : 'guest';
    }

    // 결정 2: 로그인한 사용자 - 소유권 확인
    const isOwner = user.quizUserId === quizOwnerId;

    if (isOwner) {
      // 소유자 시나리오
      return isLocked ? 'owner-locked' : 'owner';
    } else {
      // 뷰어 시나리오
      return isLocked ? 'viewer-locked' : 'viewer';
    }
  }, [user, quizOwnerId, isLocked]);

  return { scenario };
};
